// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import SignaturePad from './SignaturePad'
import { AppTextField, CheckboxField } from './FormFields'

/**
 * D-ESIGN-1 §5 — the in-app signing standard, one component for every signature: the intent statement
 * (shown verbatim, ticked), the typed full name (always), an optional drawn mark on the shared pad, and
 * re-authentication at the moment of signing — the authenticator code when the account has MFA on, else
 * the account password. Controlled: the parent owns the value and decides when it is complete
 * (`isSignatureComplete`).
 */
export interface SignatureValue {
  intentAccepted: boolean
  typedName: string
  drawing: string | null
  password: string
  totpCode: string
}

export const EMPTY_SIGNATURE: SignatureValue = { intentAccepted: false, typedName: '', drawing: null, password: '', totpCode: '' }

/** Re-auth: 'totp' = authenticator code, 'password' = account password, 'none' = not asked (a mark only). */
export type ReauthKind = 'totp' | 'password' | 'none'

export function isSignatureComplete(v: SignatureValue, reauth: ReauthKind): boolean {
  if (!v.intentAccepted || v.typedName.trim().length < 2) return false
  if (reauth === 'totp') return /^\d{6}$/.test(v.totpCode.trim())
  if (reauth === 'password') return v.password.length > 0
  return true
}

export interface SignatureCaptureProps {
  value: SignatureValue
  onChange: (v: SignatureValue) => void
  intentText: string
  reauth: ReauthKind
  /** Who re-authenticates, when it is not the signer (in person: the witnessing nurse). */
  reauthLabel?: string
  nameLabel?: string
  disabled?: boolean
}

export default function SignatureCapture({ value, onChange, intentText, reauth, reauthLabel, nameLabel = 'Type your full name', disabled = false }: SignatureCaptureProps) {
  const set = (patch: Partial<SignatureValue>) => onChange({ ...value, ...patch })
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 2, p: 1.5, background: 'var(--color-bg)' }}>
        <Typography variant="body2" data-testid="intent-text" sx={{ fontSize: 13, lineHeight: 1.5 }}>{intentText}</Typography>
        <CheckboxField checked={value.intentAccepted} onChange={v => set({ intentAccepted: v })} disabled={disabled}
          label="I agree, and I intend this to be my signature" />
      </Box>
      <AppTextField label={nameLabel} size="small" value={value.typedName} disabled={disabled} autoComplete="off"
        onChange={e => set({ typedName: e.target.value })} inputProps={{ 'aria-label': nameLabel, maxLength: 200 }} />
      <Box>
        <Typography variant="caption" sx={{ color: 'var(--color-text-soft)', fontWeight: 600 }}>
          Draw your signature (optional — the typed name is your signature)
        </Typography>
        <SignaturePad onChange={d => set({ drawing: d })} disabled={disabled} />
      </Box>
      {reauth === 'totp' && (
        <AppTextField label={reauthLabel ?? 'Authenticator code'} size="small" value={value.totpCode} disabled={disabled}
          autoComplete="one-time-code" onChange={e => set({ totpCode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
          inputProps={{ inputMode: 'numeric', 'aria-label': reauthLabel ?? 'Authenticator code' }}
          helperText="Re-enter the 6-digit code from your authenticator app to sign." />
      )}
      {reauth === 'password' && (
        <AppTextField label={reauthLabel ?? 'Password'} size="small" type="password" value={value.password} disabled={disabled}
          autoComplete="current-password" onChange={e => set({ password: e.target.value })}
          inputProps={{ 'aria-label': reauthLabel ?? 'Password' }}
          helperText="Re-enter your OwlRx password to sign." />
      )}
    </Box>
  )
}
